<template>
  <section class="calendar-panel">
    <div class="calendar-header">
      <div>
        <p class="panel-kicker">Calendar</p>
        <h2>{{ headerLabel }}</h2>
      </div>
      <div class="calendar-controls">
        <button type="button" class="btn-ghost" @click="goPrev">Prev</button>
        <button type="button" class="btn-ghost" @click="goToday">Today</button>
        <button type="button" class="btn-ghost" @click="goNext">Next</button>
      </div>
      <div class="view-switch">
        <button
          v-for="option in viewOptions"
          :key="option"
          type="button"
          :class="['chip', { active: view === option }]"
          @click="view = option"
        >
          {{ option }}
        </button>
      </div>
    </div>

    <div v-if="view === 'Month'" class="month-grid">
      <div class="weekday" v-for="day in weekdays" :key="day">
        {{ day }}
      </div>
      <div
        v-for="day in monthDays"
        :key="day.key"
        :class="['day-cell', { dim: !day.inMonth, today: day.isToday }]"
        role="button"
        tabindex="0"
        @click="switchToDay(day.date)"
        @keydown.enter="switchToDay(day.date)"
      >
        <span class="day-number">{{ day.date.getDate() }}</span>
        <div class="task-list">
          <div v-for="task in day.tasks" :key="taskKey(task)" class="task-pill">
            {{ task.title }}
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="view === 'Week'" class="week-list">
      <div
        v-for="day in weekDays"
        :key="day.key"
        class="week-row"
        role="button"
        tabindex="0"
        @click="switchToDay(day.date)"
        @keydown.enter="switchToDay(day.date)"
      >
        <div class="week-date">
          <span class="day-number">{{ day.date.getDate() }}</span>
          <span class="weekday-label">{{ weekdayLabel(day.date) }}</span>
        </div>
        <div class="week-tasks">
          <div v-for="task in day.tasks" :key="taskKey(task)" class="task-pill">
            {{ task.title }}
          </div>
        </div>
      </div>
    </div>

    <div v-else class="day-view">
      <div class="day-view-header">
        {{ dayLabel(selectedDate) }}
      </div>
      <div v-if="dayTasks.length === 0" class="panel-empty">
        No tasks for this day.
      </div>
      <div v-else class="day-task-list">
        <article v-for="task in dayTasks" :key="taskKey(task)" class="day-task">
          <div class="day-task-title">{{ task.title }}</div>
          <div v-if="task.description" class="day-task-desc">
            {{ task.description }}
          </div>
          <div class="day-task-meta">
            <span v-if="task.category">{{ task.category }}</span>
            <span class="priority" :data-priority="task.priority">
              {{ task.priority }}
            </span>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useDashboardStore } from "@/stores/dashboard";
import type { Task } from "@/types/models";

type ViewMode = "Month" | "Week" | "Day";

const dashboardStore = useDashboardStore();
const viewOptions: ViewMode[] = ["Month", "Week", "Day"];
const view = ref<ViewMode>("Month");
const selectedDate = ref(new Date());

const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const tasksWithDate = computed(() =>
  dashboardStore.tasks.filter((task) => Boolean(task.dueDate)),
);

const formatKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseTaskDate = (task: Task): Date | null => {
  if (!task.dueDate) return null;
  const date = new Date(task.dueDate);
  return Number.isNaN(date.getTime()) ? null : date;
};

const taskKey = (task: Task): string => {
  return `${task.id ?? task.title}-${task.dueDate ?? "none"}`;
};

const tasksByDate = computed(() => {
  const map = new Map<string, Task[]>();
  tasksWithDate.value.forEach((task) => {
    const date = parseTaskDate(task);
    if (!date) return;
    const key = formatKey(date);
    const list = map.get(key) ?? [];
    list.push(task);
    map.set(key, list);
  });
  return map;
});

const monthDays = computed(() => {
  const base = new Date(selectedDate.value);
  const start = new Date(base.getFullYear(), base.getMonth(), 1);
  const startWeekday = start.getDay();
  const days: Array<{
    key: string;
    date: Date;
    inMonth: boolean;
    isToday: boolean;
    tasks: Task[];
  }> = [];

  const todayKey = formatKey(new Date());

  for (let i = 0; i < 42; i += 1) {
    const date = new Date(start);
    date.setDate(start.getDate() - startWeekday + i);
    const key = formatKey(date);
    days.push({
      key,
      date,
      inMonth: date.getMonth() === start.getMonth(),
      isToday: key === todayKey,
      tasks: tasksByDate.value.get(key) ?? [],
    });
  }

  return days;
});

const weekDays = computed(() => {
  const base = new Date(selectedDate.value);
  const start = new Date(base);
  start.setDate(base.getDate() - base.getDay());

  const days: Array<{ key: string; date: Date; tasks: Task[] }> = [];
  for (let i = 0; i < 7; i += 1) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    const key = formatKey(date);
    days.push({
      key,
      date,
      tasks: tasksByDate.value.get(key) ?? [],
    });
  }

  return days;
});

const dayTasks = computed(() => {
  const key = formatKey(selectedDate.value);
  return tasksByDate.value.get(key) ?? [];
});

const headerLabel = computed(() => {
  const date = selectedDate.value;
  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    year: "numeric",
  };
  if (view.value === "Day") {
    return date.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }
  if (view.value === "Week") {
    const start = weekDays.value[0]?.date;
    const end = weekDays.value[6]?.date;
    if (start && end) {
      return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
    }
  }
  return date.toLocaleDateString(undefined, options);
});

const goPrev = (): void => {
  const date = new Date(selectedDate.value);
  if (view.value === "Month") {
    date.setMonth(date.getMonth() - 1);
  } else if (view.value === "Week") {
    date.setDate(date.getDate() - 7);
  } else {
    date.setDate(date.getDate() - 1);
  }
  selectedDate.value = date;
};

const goNext = (): void => {
  const date = new Date(selectedDate.value);
  if (view.value === "Month") {
    date.setMonth(date.getMonth() + 1);
  } else if (view.value === "Week") {
    date.setDate(date.getDate() + 7);
  } else {
    date.setDate(date.getDate() + 1);
  }
  selectedDate.value = date;
};

const goToday = (): void => {
  selectedDate.value = new Date();
};

const weekdayLabel = (date: Date): string => {
  return date.toLocaleDateString(undefined, { weekday: "short" });
};

const dayLabel = (date: Date): string => {
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
};

const switchToDay = (date: Date): void => {
  selectedDate.value = new Date(date);
  view.value = "Day";
};
</script>

<style scoped>
.calendar-panel {
  width: 100%;
  height: 100%;
  padding: 16px;
  border-radius: 16px;
  border: 1px solid #e6e6e6;
  background: white;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  overflow: hidden;
}

.calendar-header {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
}

.panel-kicker {
  margin: 0;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7a7a7a;
}

.calendar-header h2 {
  margin: 4px 0 0 0;
  font-size: 18px;
  font-weight: 600;
  color: #222;
}

.calendar-controls {
  display: flex;
  gap: 8px;
}

.btn-ghost {
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid #e0e0e0;
  background: transparent;
  font-size: 12px;
  cursor: pointer;
  color: #333;
}

.view-switch {
  display: flex;
  gap: 6px;
}

.chip {
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid #e0e0e0;
  background: #f8f8f8;
  font-size: 12px;
  cursor: pointer;
}

.chip.active {
  background: #1f73e6;
  color: white;
  border-color: #1f73e6;
}

.month-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.weekday {
  font-size: 11px;
  text-transform: uppercase;
  color: #777;
  letter-spacing: 0.08em;
}

.day-cell {
  min-height: 84px;
  border-radius: 12px;
  border: 1px solid #f0f0f0;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #fbfbfb;
  overflow: hidden;
}

.day-cell.today {
  border-color: #1f73e6;
  background: #f2f7ff;
}

.day-cell.dim {
  opacity: 0.5;
}

.day-number {
  font-size: 12px;
  font-weight: 600;
}

.task-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
}

.task-pill {
  font-size: 10px;
  padding: 4px 6px;
  border-radius: 999px;
  background: #e9f2ff;
  color: #1f73e6;
}

.week-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.week-row {
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 12px;
  align-items: flex-start;
  padding: 10px;
  border-radius: 12px;
  border: 1px solid #f0f0f0;
  background: #fbfbfb;
}

.week-date {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #333;
}

.weekday-label {
  color: #777;
  font-size: 11px;
}

.week-tasks {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.day-view {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.day-view-header {
  font-size: 14px;
  font-weight: 600;
}

.day-task-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.day-task {
  padding: 12px;
  border-radius: 12px;
  border: 1px solid #f0f0f0;
  background: #fbfbfb;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.day-task-title {
  font-weight: 600;
  font-size: 13px;
}

.day-task-desc {
  font-size: 12px;
  color: #444;
}

.day-task-meta {
  display: flex;
  gap: 8px;
  font-size: 11px;
  color: #666;
}

.priority[data-priority="high"] {
  color: #d33b27;
}

.priority[data-priority="medium"] {
  color: #f29900;
}

.priority[data-priority="low"] {
  color: #2e7d32;
}

.panel-empty {
  color: #777;
  font-size: 12px;
}
</style>
